import React, { useState } from "react"
import { Table } from "reactstrap"

import { CONSOLIDATED_FIELDS, GROUP_APPT_FIELDS, GROUP_TOP_FIELDS } from "./constants"

/**
 * Tier 1 comparison: the fields the strategy's consolidate step actually returns, in the
 * backend's own order. No diff library — the field set is small, closed and entirely
 * decision-relevant, so an aligned two-column table reads faster than a text diff and cannot
 * invent noise.
 *
 * Two shapes, because a group reply consolidates differently: GroupApptConfirmationStrategy
 * returns {appointments: [...], appointments_confirmed, ...} with the per-appointment
 * decision inside each entry, where the single-appointment strategy returns 17 flat fields.
 * The shape is detected from the payload, matching how the server decides which diff to run.
 *
 * changedFields comes from the SERVER, never recomputed here. The backend normalizes before
 * diffing (_normalize_llm_status rewrites "" -> None for confirmation_code; the intersect
 * helpers settle list ordering), so a browser-side comparison of raw values would report
 * ordering and empty-value differences that production does not consider differences at all.
 */
function renderValue(v) {
  if (v === null || v === undefined) {
    // Shown explicitly: a null conf_date_tm_from and an absent key are different situations,
    // and null is a common, meaningful value in this dict.
    return <span className="text-muted fst-italic">null</span>
  }
  if (typeof v === "boolean") return String(v)
  if (Array.isArray(v) || typeof v === "object") {
    return <code className="small">{JSON.stringify(v)}</code>
  }
  if (v === "") return <span className="text-muted fst-italic">&quot;&quot;</span>
  return String(v)
}

const isGroup = s => !!s && typeof s === "object" && "appointments" in s

/** Appointments keyed the way the server pairs them: row_index, else list position. */
function appointmentsByKey(status) {
  const out = new Map()
  const list = (status && status.appointments) || []
  list.forEach((appt, i) => {
    if (!appt || typeof appt !== "object") return
    out.set(appt.row_index !== null && appt.row_index !== undefined ? appt.row_index : `#${i}`,
            appt)
  })
  return out
}

/**
 * The flat (single-appointment) row set, and the group one, as a single list of
 * {key, label, indent} so one table body renders either. Group keys match the server's
 * changed_fields naming exactly ("appt[2].status"), which is what makes the highlight work
 * without the client re-deriving anything.
 */
function buildRows(before, after) {
  if (!isGroup(before) && !isGroup(after)) {
    return CONSOLIDATED_FIELDS.map(f => ({ ...f, get: s => (s ? s[f.key] : null) }))
  }
  const rows = GROUP_TOP_FIELDS.map(f => ({ ...f, get: s => (s ? s[f.key] : null) }))
  // Built once, not per rendered cell: both sides are walked here and the row closures just
  // read out of the resulting maps.
  const b = appointmentsByKey(before)
  const a = appointmentsByKey(after)
  const keys = [...new Set([...b.keys(), ...a.keys()])]
    .sort((x, y) => String(x).localeCompare(String(y)))
  keys.forEach(k => {
    rows.push({ key: `appt[${k}]`, label: `appointment ${k}`, heading: true })
    GROUP_APPT_FIELDS.forEach(f => {
      rows.push({
        key: `appt[${k}].${f.key}`,
        label: f.label,
        indent: true,
        get: s => {
          const appt = (s === before ? b : a).get(k)
          return appt ? appt[f.key] : null
        },
      })
    })
  })
  return rows
}

function FieldDiff({ before, after, changedFields }) {
  const changed = new Set(changedFields || [])
  const [onlyChanged, setOnlyChanged] = useState(false)

  const allRows = buildRows(before, after)
  // A heading is kept whenever anything under it is kept, so a filtered group table does not
  // show orphaned fields with no appointment to attach them to.
  const rows = !onlyChanged
    ? allRows
    : allRows.filter((f, i) =>
        changed.has(f.key) ||
        (f.heading && allRows.some((g, j) => j > i && !g.heading &&
          g.key.startsWith(`${f.key}.`) && changed.has(g.key))))
  const fieldCount = allRows.filter(f => !f.heading).length

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-1">
        <small className="text-muted">
          {changed.size} of {fieldCount} decision fields changed
        </small>
        <label className="small mb-0">
          <input
            type="checkbox"
            className="me-1"
            checked={onlyChanged}
            onChange={e => setOnlyChanged(e.target.checked)}
          />
          hide unchanged
        </label>
      </div>
      <Table bordered size="sm" className="mb-0 small">
        <thead className="table-light">
          <tr>
            <th style={{ width: "32%" }}>field</th>
            <th style={{ width: "34%" }}>before</th>
            <th style={{ width: "34%" }}>after</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(f => {
            const isChanged = changed.has(f.key)
            if (f.heading) {
              return (
                <tr key={f.key} className="table-secondary">
                  <td colSpan={3} className="fw-bold">
                    {f.label}
                    {isChanged && (
                      <span className="fw-normal ms-2">
                        — present in only one of the two runs
                      </span>
                    )}
                  </td>
                </tr>
              )
            }
            return (
              <tr key={f.key} className={isChanged ? "table-warning" : undefined}>
                <td className={isChanged ? "fw-bold" : "text-muted"}>
                  {isChanged ? "◀ " : ""}
                  {f.indent ? <span className="ms-3">{f.label}</span> : f.label}
                </td>
                <td>{renderValue(f.get(before))}</td>
                <td className={isChanged ? "fw-bold" : undefined}>
                  {renderValue(f.get(after))}
                </td>
              </tr>
            )
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={3} className="text-muted text-center">
                No fields changed.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  )
}

export default FieldDiff
